import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '@/providers/AuthProvider';
import { useProfile } from '@/services/profiles';
import styles from './SharePage.module.css';

const deriveProfileId = (user: ReturnType<typeof useAuth>['user']) => {
  if (!user) {
    return undefined;
  }
  const candidate = user as { sub?: string; email?: string };
  return candidate.sub ?? candidate.email ?? undefined;
};

const buildShareUrl = (shortId?: string, fallbackId?: string) => {
  if (typeof window === 'undefined') {
    return '';
  }
  const identifier = shortId ?? fallbackId;
  if (!identifier) {
    return '';
  }
  const origin = window.location.origin;
  return `${origin}?refId=${encodeURIComponent(identifier)}`;
};

const buildQrUrl = (shareUrl: string) =>
  shareUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?format=svg&data=${encodeURIComponent(
        shareUrl,
      )}`
    : '';

const SharePage = () => {
  const { user, isAuthenticated, isLoading: authLoading, login } = useAuth();
  const profileId = useMemo(() => deriveProfileId(user), [user]);
  const {
    data: profile,
    isLoading: profileLoading,
    isError,
    refetch,
  } = useProfile(profileId ?? '', {
    enabled: Boolean(profileId) && isAuthenticated,
  });

  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');

  const shareUrl = useMemo(
    () => buildShareUrl(profile?.shortid, profile?.id ?? profileId),
    [profile?.shortid, profile?.id, profileId],
  );

  const qrUrl = useMemo(() => buildQrUrl(shareUrl), [shareUrl]);

  const handleCopy = async () => {
    if (!shareUrl) {
      return;
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 2000);
    } catch (error) {
      console.warn('Unable to copy share link', error);
      setCopyState('error');
      setTimeout(() => setCopyState('idle'), 3000);
    }
  };

  if (authLoading) {
    return (
      <div className={styles.page}>
        <section className={styles.card}>
          <p>Preparing share tools…</p>
        </section>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className={styles.page}>
        <section className={styles.card}>
          <h1>Sign in to share AwkHerd</h1>
          <p>You need to be logged in to share your referral link.</p>
          <button type="button" className={styles.primaryButton} onClick={() => login()}>
            Sign in
          </button>
        </section>
      </div>
    );
  }

  if (profileLoading) {
    return (
      <div className={styles.page}>
        <section className={styles.card}>
          <p>Fetching your share link…</p>
        </section>
      </div>
    );
  }

  if (isError || !shareUrl) {
    return (
      <div className={styles.page}>
        <section className={styles.card}>
          <h1>Unable to build share link</h1>
          <p>We couldn&apos;t fetch your profile details. Try again in a moment.</p>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={() => refetch()}
          >
            Retry
          </button>
        </section>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <Helmet>
        <title>Share AwkHerd • AwkHerd</title>
      </Helmet>
      <section className={styles.card}>
        <header className={styles.header}>
          <div>
            <h1>Share AwkHerd</h1>
            <p>Invite friends with your unique link or the QR code below.</p>
          </div>
          <button type="button" className={styles.secondaryButton} onClick={handleCopy}>
            {copyState === 'copied'
              ? 'Link copied'
              : copyState === 'error'
                ? 'Copy failed'
                : 'Copy link'}
          </button>
        </header>

        <div className={styles.content}>
          <div className={styles.qrWrap}>
            {qrUrl ? (
              <img src={qrUrl} alt="AwkHerd share QR code" />
            ) : (
              <p className={styles.notice}>Unable to generate QR code right now.</p>
            )}
          </div>

          <div className={styles.details}>
            <label htmlFor="share-url">Share link</label>
            <input id="share-url" readOnly value={shareUrl} />
            <ul>
              <li>Scanning the code takes friends to AwkHerd with your referral ID.</li>
              <li>
                New users will be guided through sign up and land on Discover with your
                profile highlighted.
              </li>
              <li>
                Existing users jump straight to Discover with your profile in focus.
              </li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SharePage;
